import React, { useState, useMemo, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { LogoWithDomainSwitcher } from '@/shared/components/layout/LogoWithDomainSwitcher';
import { useSystemUsers } from '@/shared/hooks/useSystemUsers';
import { useAuthStore } from '@/shared/stores/authStore';
import { resolveSystemUserName } from '@/shared/classifiers/display/user-name';
import {
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  X,
  Activity,
  FolderKanban,
  Users,
  Star,
  Building2
} from 'lucide-react';
import { fetchWorkflows, type WorkflowWithOrg } from '@/shared/services/intel/workflowDataService';
import { fetchAllTeamMembers, type TeamMemberData } from '@/shared/services/intel/teamMemberDataService';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ForwardRefExoticComponent<any>;
  shortName?: string;
}

/**
 * IntelSidebar component - Navigation sidebar for intel domain
 * Responsive design with mobile support
 */
const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, isCollapsed, onToggleCollapse }) => {
  const { effectiveUser, hasGroupRouteAccess, hasGroupCapability } = useAuthStore();
  const { users } = useSystemUsers();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [isProjectsExpanded, setIsProjectsExpanded] = useState(false);
  const [isTeamMembersExpanded, setIsTeamMembersExpanded] = useState(false);
  const [isCompanyExpanded, setIsCompanyExpanded] = useState(false);
  const [workflows, setWorkflows] = useState<WorkflowWithOrg[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMemberData[]>([]);
  const [hoveredDropdown, setHoveredDropdown] = useState<'projects' | 'team-members' | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const projectsButtonRef = useRef<HTMLButtonElement>(null);
  const teamMembersButtonRef = useRef<HTMLButtonElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Update dropdown position when hovering
  const updateDropdownPosition = (buttonRef: React.RefObject<HTMLButtonElement | null>) => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.top,
        left: rect.right + 2 // 2px gap from button (closer)
      });
    }
  };

  // Clear any pending close timeout
  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  // Schedule dropdown close with delay
  const scheduleClose = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredDropdown(null);
    }, 150); // 150ms delay to allow cursor movement
  };

  // Get current user's starred workflows
  const currentUser = useMemo(() => {
    if (!effectiveUser?.employee_id) return null;
    return users.find(u => u.employee_id === effectiveUser.employee_id);
  }, [users, effectiveUser]);

  const starredWorkflowIds = currentUser?.starred_workflows || [];
  const starredUserIds = currentUser?.starred_users || [];

  // Load workflows and team members on mount (always load so we have data when items are starred)
  React.useEffect(() => {
    const loadData = async () => {
      try {
        const [workflowsData, teamMembersData] = await Promise.all([
          fetchWorkflows(),
          fetchAllTeamMembers()
        ]);
        setWorkflows(workflowsData);
        setTeamMembers(teamMembersData);
      } catch (error) {
        // Error loading sidebar data — silently degrade
      }
    };
    
    loadData();
  }, []); // Load once on mount

  // Auto-expand sections when on their routes
  React.useEffect(() => {
    if (location.pathname.startsWith('/intel/projects')) {
      setIsProjectsExpanded(true);
    }
    if (location.pathname.startsWith('/intel/team-members')) {
      setIsTeamMembersExpanded(true);
    }
    if (location.pathname.startsWith('/intel/company')) {
      setIsCompanyExpanded(true);
    }
  }, [location.pathname]);

  // Filter workflows to only starred ones
  const starredWorkflows = useMemo(() => {
    return workflows.filter(wf => starredWorkflowIds.includes(wf.workflow_id));
  }, [workflows, starredWorkflowIds]);

  // Filter team members to only starred ones with system user data
  const starredTeamMembers = useMemo(() => {
    const starredUsers = teamMembers.filter(tm => starredUserIds.includes(tm.user_id));
    // Enrich with system user data for names and avatars
    return starredUsers.map(tm => {
      const systemUser = users.find(u => u.employee_id.toString() === tm.user_id);
      return {
        ...tm,
        name: resolveSystemUserName(systemUser, tm.user_email),
        avatar_url: systemUser?.employee_data?.cached_avatar_url || systemUser?.employee_data?.avatar
      };
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [teamMembers, starredUserIds, users]);

  // Navigation items for intel domain
  const getNavigationItems = (): NavigationItem[] => {
    const intelItems: NavigationItem[] = [
      {
        name: 'Overview',
        href: '/intel/overview',
        icon: Activity,
        shortName: 'Overview'
      },
      {
        name: 'Company',
        href: '/intel/company',
        icon: Building2,
        shortName: 'Company'
      },
      {
        name: 'Projects',
        href: '/intel/projects',
        icon: FolderKanban,
        shortName: 'Projects'
      },
      {
        name: 'Team Members',
        href: '/intel/team-members',
        icon: Users,
        shortName: 'Team Members'
      },
    ];

    return intelItems.filter(item => hasGroupRouteAccess(item.href));
  };

  const navigationItems = getNavigationItems();

  const isActiveRoute = (href: string) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const handleWorkflowClick = (workflow: WorkflowWithOrg) => {
    // Extract current page if we're already in a project view
    // NEW URL STRUCTURE: /intel/projects/{page}/org/:orgId/workflow/:workflowId
    const getCurrentPage = (): string => {
      const match = location.pathname.match(/\/projects\/([^/]+)\/org\//);
      return match ? match[1] : 'overview';
    };

    const currentPage = getCurrentPage();
    
    // Navigate to workflow using new page-first URL structure
    navigate(`/intel/projects/${currentPage}/org/${workflow.org_id}/workflow/${workflow.workflow_id}`);
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  const handleTeamMemberClick = (userId: string) => {
    // Extract current page if we're already in a team member view
    // NEW URL STRUCTURE: /intel/team-members/{page}/:userId
    const getCurrentPage = (): string => {
      const match = location.pathname.match(/\/team-members\/([^/]+)\//);
      return match ? match[1] : 'overview';
    };

    const currentPage = getCurrentPage();
    
    // Navigate to team member using new page-first URL structure
    navigate(`/intel/team-members/${currentPage}/${userId}`);
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar - Floating Container */}
      <div
        className={`fixed z-50 bg-white transform transition-all duration-300 ease-in-out
          lg:left-4 lg:top-4 lg:bottom-4 lg:rounded-2xl lg:shadow-2xl lg:translate-x-0
          inset-y-0 left-0 shadow-lg
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isCollapsed ? 'w-20' : 'w-72'}
        `}
      >
        <div className="flex flex-col h-full lg:rounded-2xl overflow-hidden">
          {/* Sidebar header - Mobile close button only */}
          <div className="flex items-center justify-end p-4 border-b border-gray-200 lg:hidden">
            <button
              onClick={onClose}
              className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Collapse toggle button - Desktop only, positioned outside overflow container */}
          <button
            onClick={onToggleCollapse}
            className="hidden lg:flex absolute -right-3 top-[68px] z-[60] w-6 h-6 items-center justify-center bg-white rounded-full border border-gray-200 shadow-md hover:shadow-lg hover:border-gray-300 hover:scale-110 transition-all cursor-pointer"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            type="button"
          >
            {isCollapsed ? (
              <ChevronRight className="h-3.5 w-3.5 text-gray-600" />
            ) : (
              <ChevronLeft className="h-3.5 w-3.5 text-gray-600" />
            )}
          </button>

          {/* Logo with integrated Domain Switcher */}
          <LogoWithDomainSwitcher isCollapsed={isCollapsed} />

          {/* Navigation */}
          <nav className="flex-1 px-4 py-5 space-y-1 overflow-y-auto">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = isActiveRoute(item.href);
              const isProjects = item.name === 'Projects';
              const isTeamMembers = item.name === 'Team Members';
              const isCompany = item.name === 'Company';
              
              return (
                <div key={item.name}>
                  <div className="relative">
                    {/* Projects button - toggles expansion when starred workflows exist */}
                    {isProjects && starredWorkflows.length > 0 ? (
                      <div
                        className="relative"
                        onMouseEnter={() => {
                          if (isCollapsed) {
                            clearCloseTimeout();
                            updateDropdownPosition(projectsButtonRef);
                            setHoveredDropdown('projects');
                          }
                        }}
                        onMouseLeave={() => isCollapsed && scheduleClose()}
                      >
                        <button
                          ref={projectsButtonRef}
                          onClick={() => !isCollapsed && setIsProjectsExpanded(!isProjectsExpanded)}
                          className={`w-full group flex items-center text-sm font-medium rounded-lg transition-all ${
                            isCollapsed ? 'px-3 py-3 justify-center' : 'px-3 py-2'
                          } ${
                            isActive
                              ? 'bg-gradient-to-r from-[#5b3d72] to-[#704a8b] text-white shadow-md'
                              : 'text-gray-600 hover:bg-[#ebe4f1]/50 hover:text-[#311c3b]'
                          }`}
                          title={item.name}
                        >
                          <Icon
                            className={`h-5 w-5 transition-colors ${
                              isCollapsed ? '' : 'mr-3'
                            } ${
                              isActive ? 'text-white' : 'text-gray-400 group-hover:text-[#311c3b]'
                            }`}
                          />
                          {!isCollapsed && (
                            <>
                              <span className="flex-1 text-left">
                                {item.shortName || item.name}
                              </span>
                              {isProjectsExpanded ? (
                                <ChevronDown className={`h-4 w-4 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                              ) : (
                                <ChevronRight className={`h-4 w-4 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                              )}
                            </>
                          )}
                        </button>
                        
                        {/* Collapsed dropdown menu for Projects */}
                        {isCollapsed && hoveredDropdown === 'projects' && (
                          <div
                            className="fixed w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-[5000]"
                            style={{
                              left: `${dropdownPosition.left}px`,
                              top: `${dropdownPosition.top}px`
                            }}
                            onMouseEnter={() => clearCloseTimeout()}
                            onMouseLeave={() => scheduleClose()}
                          >
                            <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                              Projects
                            </div>
                            <NavLink
                              to="/intel/projects"
                              onClick={() => {
                                setHoveredDropdown(null);
                                if (window.innerWidth < 1024) {
                                  onClose();
                                }
                              }}
                              className={`flex items-center px-3 py-2 text-sm transition-colors ${
                                location.pathname === '/intel/projects'
                                  ? 'bg-purple-50 text-purple-900 font-medium'
                                  : 'text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              <FolderKanban className={`mr-3 h-4 w-4 ${
                                location.pathname === '/intel/projects' ? 'text-purple-600' : 'text-gray-400'
                              }`} />
                              All Projects
                            </NavLink>
                            {starredWorkflows.length > 0 && (
                              <>
                                <div className="px-3 py-1 mt-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                  Starred
                                </div>
                                {starredWorkflows.map((workflow) => {
                                  // NEW URL STRUCTURE: Check if path contains this workflow
                                  const workflowPattern = `/org/${workflow.org_id}/workflow/${workflow.workflow_id}`;
                                  const isWorkflowActive = location.pathname.includes(workflowPattern);
                                  
                                  return (
                                    <button
                                      key={workflow.workflow_id}
                                      onClick={() => {
                                        handleWorkflowClick(workflow);
                                        setHoveredDropdown(null);
                                      }}
                                      className={`w-full flex items-center px-3 py-2 text-sm transition-colors ${
                                        isWorkflowActive
                                          ? 'bg-purple-50 text-purple-900'
                                          : 'text-gray-700 hover:bg-gray-50'
                                      }`}
                                      title={workflow.workflow_name}
                                    >
                                      <Star className={`mr-3 h-3.5 w-3.5 flex-shrink-0 ${
                                        isWorkflowActive ? 'text-yellow-500 fill-yellow-500' : 'text-yellow-400 fill-yellow-400'
                                      }`} />
                                      <span className="truncate text-left">{workflow.workflow_name}</span>
                                    </button>
                                  );
                                })}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    ) : isTeamMembers && starredTeamMembers.length > 0 ? (
                      <div
                        className="relative"
                        onMouseEnter={() => {
                          if (isCollapsed) {
                            clearCloseTimeout();
                            updateDropdownPosition(teamMembersButtonRef);
                            setHoveredDropdown('team-members');
                          }
                        }}
                        onMouseLeave={() => isCollapsed && scheduleClose()}
                      >
                        <button
                          ref={teamMembersButtonRef}
                          onClick={() => !isCollapsed && setIsTeamMembersExpanded(!isTeamMembersExpanded)}
                          className={`w-full group flex items-center text-sm font-medium rounded-lg transition-all ${
                            isCollapsed ? 'px-3 py-3 justify-center' : 'px-3 py-2'
                          } ${
                            isActive
                              ? 'bg-gradient-to-r from-[#5b3d72] to-[#704a8b] text-white shadow-md'
                              : 'text-gray-600 hover:bg-[#ebe4f1]/50 hover:text-[#311c3b]'
                          }`}
                          title={item.name}
                        >
                          <Icon
                            className={`h-5 w-5 transition-colors ${
                              isCollapsed ? '' : 'mr-3'
                            } ${
                              isActive ? 'text-white' : 'text-gray-400 group-hover:text-[#311c3b]'
                            }`}
                          />
                          {!isCollapsed && (
                            <>
                              <span className="flex-1 text-left">
                                {item.shortName || item.name}
                              </span>
                              {isTeamMembersExpanded ? (
                                <ChevronDown className={`h-4 w-4 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                              ) : (
                                <ChevronRight className={`h-4 w-4 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                              )}
                            </>
                          )}
                        </button>
                        
                        {/* Collapsed dropdown menu for Team Members */}
                        {isCollapsed && hoveredDropdown === 'team-members' && (
                          <div
                            className="fixed w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-[5000]"
                            style={{
                              left: `${dropdownPosition.left}px`,
                              top: `${dropdownPosition.top}px`
                            }}
                            onMouseEnter={() => clearCloseTimeout()}
                            onMouseLeave={() => scheduleClose()}
                          >
                            <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                              Team Members
                            </div>
                            <NavLink
                              to="/intel/team-members"
                              onClick={() => {
                                setHoveredDropdown(null);
                                if (window.innerWidth < 1024) {
                                  onClose();
                                }
                              }}
                              className={`flex items-center px-3 py-2 text-sm transition-colors ${
                                location.pathname === '/intel/team-members'
                                  ? 'bg-purple-50 text-purple-900 font-medium'
                                  : 'text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              <Users className={`mr-3 h-4 w-4 ${
                                location.pathname === '/intel/team-members' ? 'text-purple-600' : 'text-gray-400'
                              }`} />
                              All Team Members
                            </NavLink>
                            {starredTeamMembers.length > 0 && (
                              <>
                                <div className="px-3 py-1 mt-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                  Starred
                                </div>
                                {starredTeamMembers.map((member) => {
                                  // NEW URL STRUCTURE: Check if path ends with this user ID
                                  const memberPattern = `/${member.user_id}`;
                                  const isMemberActive = location.pathname.endsWith(memberPattern) ||
                                                         location.pathname.includes(`/${member.user_id}/`);
                                  
                                  return (
                                    <button
                                      key={member.user_id}
                                      onClick={() => {
                                        handleTeamMemberClick(member.user_id);
                                        setHoveredDropdown(null);
                                      }}
                                      className={`w-full flex items-center px-3 py-2 text-sm transition-colors ${
                                        isMemberActive
                                          ? 'bg-purple-50 text-purple-900'
                                          : 'text-gray-700 hover:bg-gray-50'
                                      }`}
                                      title={member.name}
                                    >
                                      <Star className={`mr-3 h-3.5 w-3.5 flex-shrink-0 ${
                                        isMemberActive ? 'text-yellow-500 fill-yellow-500' : 'text-yellow-400 fill-yellow-400'
                                      }`} />
                                      <span className="truncate text-left">{member.name}</span>
                                    </button>
                                  );
                                })}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    ) : isCompany ? (
                      /* Company section - static sub-items, always expandable */
                      isCollapsed ? (
                        <NavLink
                          to="/intel/company/revenue-drivers"
                          onClick={() => { if (window.innerWidth < 1024) onClose(); }}
                          className={`group flex items-center text-sm font-medium rounded-lg transition-all px-3 py-3 justify-center ${
                            isActive
                              ? 'bg-gradient-to-r from-[#5b3d72] to-[#704a8b] text-white shadow-md'
                              : 'text-gray-600 hover:bg-[#ebe4f1]/50 hover:text-[#311c3b]'
                          }`}
                          title="Company"
                        >
                          <Icon
                            className={`h-5 w-5 transition-colors ${
                              isActive ? 'text-white' : 'text-gray-400 group-hover:text-[#311c3b]'
                            }`}
                          />
                        </NavLink>
                      ) : (
                        <button
                          onClick={() => setIsCompanyExpanded(!isCompanyExpanded)}
                          className={`w-full group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                            isActive
                              ? 'bg-gradient-to-r from-[#5b3d72] to-[#704a8b] text-white shadow-md'
                              : 'text-gray-600 hover:bg-[#ebe4f1]/50 hover:text-[#311c3b]'
                          }`}
                          title="Company"
                        >
                          <Icon
                            className={`h-5 w-5 mr-3 transition-colors ${
                              isActive ? 'text-white' : 'text-gray-400 group-hover:text-[#311c3b]'
                            }`}
                          />
                          <span className="flex-1 text-left">Company</span>
                          {isCompanyExpanded
                            ? <ChevronDown className={`h-4 w-4 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                            : <ChevronRight className={`h-4 w-4 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                          }
                        </button>
                      )
                    ) : (
                      /* Regular NavLink for items without starred items */
                      <NavLink
                        to={item.href}
                        onClick={() => {
                          if (window.innerWidth < 1024) {
                            onClose();
                          }
                        }}
                        className={`group flex items-center text-sm font-medium rounded-lg transition-all ${
                          isCollapsed ? 'px-3 py-3 justify-center' : 'px-3 py-2'
                        } ${
                          isActive
                            ? 'bg-gradient-to-r from-[#5b3d72] to-[#704a8b] text-white shadow-md'
                            : 'text-gray-600 hover:bg-[#ebe4f1]/50 hover:text-[#311c3b]'
                        }`}
                        title={item.name}
                      >
                        <Icon
                          className={`h-5 w-5 transition-colors ${
                            isCollapsed ? '' : 'mr-3'
                          } ${
                            isActive ? 'text-white' : 'text-gray-400 group-hover:text-[#311c3b]'
                          }`}
                        />
                        {!isCollapsed && (
                          <span className="flex-1">
                            {item.shortName || item.name}
                          </span>
                        )}
                      </NavLink>
                    )}
                  </div>

                  {/* Starred Workflows Submenu with "All Projects" option */}
                  {!isCollapsed && isProjects && isProjectsExpanded && starredWorkflows.length > 0 && (
                    <div className="ml-8 mt-1 space-y-1">
                      {/* All Projects option */}
                      <NavLink
                        to="/intel/projects"
                        onClick={() => {
                          if (window.innerWidth < 1024) {
                            onClose();
                          }
                        }}
                        className={`group flex items-center px-3 py-2 text-sm rounded-lg transition-all ${
                          location.pathname === '/intel/projects'
                            ? 'bg-purple-100 text-purple-900 font-medium'
                            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                        title="All Projects"
                      >
                        <FolderKanban
                          className={`mr-2 h-3.5 w-3.5 ${
                            location.pathname === '/intel/projects' ? 'text-purple-700' : 'text-gray-400'
                          }`}
                        />
                        <span className="flex-1 text-left">
                          All Projects
                        </span>
                      </NavLink>
                      
                      {/* Starred workflows */}
                      {starredWorkflows.map((workflow) => {
                        const workflowPath = `/intel/projects/org/${workflow.org_id}/workflow/${workflow.workflow_id}`;
                        const isWorkflowActive = location.pathname.startsWith(workflowPath);
                        
                        return (
                          <button
                            key={workflow.workflow_id}
                            onClick={() => handleWorkflowClick(workflow)}
                            className={`w-full group flex items-center px-3 py-2 text-sm rounded-lg transition-all ${
                              isWorkflowActive
                                ? 'bg-purple-100 text-purple-900'
                                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                            }`}
                            title={workflow.workflow_name}
                          >
                            <Star
                              className={`mr-2 h-3.5 w-3.5 ${
                                isWorkflowActive ? 'text-yellow-500 fill-yellow-500' : 'text-yellow-400 fill-yellow-400'
                              }`}
                            />
                            <span className="flex-1 text-left truncate">
                              {workflow.workflow_name}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Company Sub-items */}
                  {!isCollapsed && isCompany && isCompanyExpanded && (
                    <div className="ml-8 mt-1 space-y-1">
                      {[
                        {
                          name: 'Analytics',
                          href: '/intel/company/revenue-drivers',
                          isSubActive: [
                            '/intel/company/revenue-drivers',
                            '/intel/company/people-drivers',
                            '/intel/company/projects',
                          ].some(p => location.pathname.startsWith(p))
                        },
                        {
                          name: 'Creative',
                          href: '/intel/company/creative',
                          isSubActive: location.pathname.startsWith('/intel/company/creative')
                        },
                        {
                          name: 'Data',
                          href: '/intel/company/data',
                          isSubActive: location.pathname.startsWith('/intel/company/data')
                        },
                        {
                          name: 'FTE Reporting',
                          href: '/intel/company/fte-reporting',
                          isSubActive: location.pathname.startsWith('/intel/company/fte-reporting')
                        },
                        ...(hasGroupCapability('finance:sensitive') ? [
                          {
                            name: 'Income',
                            href: '/intel/company/income',
                            isSubActive: location.pathname.startsWith('/intel/company/income')
                          },
                          {
                            name: 'Forecasting',
                            href: '/intel/company/forecasting',
                            isSubActive: location.pathname.startsWith('/intel/company/forecasting')
                          },
                        ] : []),
                      ].map(subItem => (
                        <NavLink
                          key={subItem.name}
                          to={subItem.href}
                          onClick={() => { if (window.innerWidth < 1024) onClose(); }}
                          className={`group flex items-center px-3 py-2 text-sm rounded-lg transition-all ${
                            subItem.isSubActive
                              ? 'bg-purple-100 text-purple-900 font-medium'
                              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                          }`}
                        >
                          {subItem.name}
                        </NavLink>
                      ))}
                    </div>
                  )}

                  {/* Starred Team Members Submenu with "All Team Members" option */}
                  {!isCollapsed && item.name === 'Team Members' && isTeamMembersExpanded && starredTeamMembers.length > 0 && (
                    <div className="ml-8 mt-1 space-y-1">
                      {/* All Team Members option */}
                      <NavLink
                        to="/intel/team-members"
                        onClick={() => {
                          if (window.innerWidth < 1024) {
                            onClose();
                          }
                        }}
                        className={`group flex items-center px-3 py-2 text-sm rounded-lg transition-all ${
                          location.pathname === '/intel/team-members'
                            ? 'bg-purple-100 text-purple-900 font-medium'
                            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                        title="All Team Members"
                      >
                        <Users
                          className={`mr-2 h-3.5 w-3.5 ${
                            location.pathname === '/intel/team-members' ? 'text-purple-700' : 'text-gray-400'
                          }`}
                        />
                        <span className="flex-1 text-left">
                          All Team Members
                        </span>
                      </NavLink>
                      
                      {/* Starred team members */}
                      {starredTeamMembers.map((member) => {
                        const memberPath = `/intel/team-members/user/${member.user_id}`;
                        const isMemberActive = location.pathname.startsWith(memberPath);
                        
                        return (
                          <button
                            key={member.user_id}
                            onClick={() => handleTeamMemberClick(member.user_id)}
                            className={`w-full group flex items-center px-3 py-2 text-sm rounded-lg transition-all ${
                              isMemberActive
                                ? 'bg-purple-100 text-purple-900'
                                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                            }`}
                            title={member.name}
                          >
                            <Star
                              className={`mr-2 h-3.5 w-3.5 ${
                                isMemberActive ? 'text-yellow-500 fill-yellow-500' : 'text-yellow-400 fill-yellow-400'
                              }`}
                            />
                            <span className="flex-1 text-left truncate">
                              {member.name}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

        </div>
      </div>
    </>
  );
};

export default Sidebar;